import React, { useState, useEffect } from 'react';
import { Customer } from '../../types/customer';
import { useCustomers } from '../../hooks/useCustomers';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
  MagnifyingGlassIcon, 
  UserIcon, 
  BuildingOffice2Icon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import LoadingScreen from './LoadingScreen';

interface CustomerSelectorProps {
  selectedCustomer?: Customer | null;
  onCustomerSelect: (customer: Customer | null) => void;
  placeholder?: string;
  className?: string;
}

const CustomerSelector: React.FC<CustomerSelectorProps> = ({
  selectedCustomer,
  onCustomerSelect,
  placeholder = "Válasszon ügyfelet...",
  className = ""
}) => {
  const { customers, loading } = useCustomers();
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);

  // Filter customers based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredCustomers(customers);
      return;
    }

    const filtered = customers.filter(customer => {
      const searchLower = searchTerm.toLowerCase();
      const name = customer.type === 'organization' 
        ? customer.companyName?.toLowerCase() || ''
        : `${customer.firstName} ${customer.lastName}`.toLowerCase();
      
      return (
        name.includes(searchLower) ||
        customer.email?.toLowerCase().includes(searchLower) ||
        customer.phone?.includes(searchTerm) ||
        customer.taxNumber?.includes(searchTerm)
      );
    });

    setFilteredCustomers(filtered);
  }, [customers, searchTerm]);

  const handleCustomerSelect = (customer: Customer) => {
    onCustomerSelect(customer);
    setIsOpen(false);
    setSearchTerm('');
  };

  const clearSelection = () => {
    onCustomerSelect(null);
    setSearchTerm('');
  };

  const getCustomerDisplayName = (customer: Customer) => {
    return customer.type === 'organization' 
      ? customer.companyName || 'Névtelen cég'
      : `${customer.lastName} ${customer.firstName}`;
  };

  const getCustomerIcon = (customer: Customer) => {
    return customer.type === 'organization' ? BuildingOffice2Icon : UserIcon;
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className={`relative ${className}`}>
      {/* Selected customer display / Search input */}
      <div className="relative">
        {selectedCustomer ? (
          <div className="flex items-center justify-between w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm">
            <div className="flex items-center space-x-2 flex-1 min-w-0">
              <div className="p-1 rounded bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 border-primary-200 dark:border-primary-700 border flex-shrink-0">
                {React.createElement(getCustomerIcon(selectedCustomer), {
                  className: "h-4 w-4 text-primary-600 dark:text-primary-400"
                })}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-gray-900 dark:text-gray-100 font-medium truncate">
                  {getCustomerDisplayName(selectedCustomer)}
                </div>
                {selectedCustomer.email && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {selectedCustomer.email}
                  </div>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={clearSelection}
              className="ml-2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setIsOpen(true)}
            className="flex items-center justify-between w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:border-primary-500 dark:hover:border-primary-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
          >
            <span className="truncate">{placeholder}</span>
            <MagnifyingGlassIcon className="h-4 w-4 ml-2 flex-shrink-0" />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-80 overflow-hidden">
          {/* Search input */}
          <div className="p-3 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Keresés név, email vagy telefon alapján..."
                className="w-full pl-10 pr-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-300 placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                autoFocus
              />
            </div>
          </div>

          {/* Customer list */}
          <div className="max-h-64 overflow-y-auto">
            {filteredCustomers.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                {searchTerm ? 'Nincs találat' : 'Nincsenek ügyfelek'}
              </div>
            ) : (
              <div className="py-1">
                {filteredCustomers.map((customer) => {
                  const Icon = getCustomerIcon(customer);
                  return (
                    <button
                      key={customer.id}
                      type="button"
                      onClick={() => handleCustomerSelect(customer)}
                      className="w-full flex items-center px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="p-1 rounded bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 border-primary-200 dark:border-primary-700 border mr-3 flex-shrink-0">
                        <Icon className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {getCustomerDisplayName(customer)}
                        </div>
                        <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
                          {customer.email && (
                            <span className="truncate">{customer.email}</span>
                          )}
                          {customer.phone && customer.email && (
                            <span>•</span>
                          )}
                          {customer.phone && (
                            <span>{customer.phone}</span>
                          )}
                        </div>
                        {customer.type === 'organization' && customer.taxNumber && (
                          <div className="text-xs text-gray-400 dark:text-gray-500 truncate">
                            Adószám: {customer.taxNumber}
                          </div>
                        )}
                      </div>
                      {selectedCustomer?.id === customer.id && (
                        <CheckIcon className="h-4 w-4 text-primary-600 dark:text-primary-400 ml-2 flex-shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Close button */}
          <div className="p-3 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="w-full px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Bezárás
            </button>
          </div>
        </div>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default CustomerSelector;
